import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/shared/components/ui/Label/Label';
import { Switch } from '@/shared/components/ui/Switch/Switch';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  SELECTION_NOTICE_CLASSES,
  TOGGLE_GROUP_CLASSES,
  TOGGLE_GROUP_LABEL_CLASSES,
  TOGGLE_GROUP_ROW_CLASSES,
  TOGGLE_GROUP_ROW_TEXT_CLASSES,
  TOGGLE_GROUP_DESCRIPTION_CLASSES,
} from '../selection.tokens';

import { type ToggleGroupProps } from './ToggleGroup.types';
import { useToggleGroupLogic } from './useToggleGroupLogic';

/**
 * A named set of independent switches over one object value.
 *
 * It lives in `selection/` rather than in `ui/` because that is what it is —
 * several values chosen out of a closed, already-translated list, the same
 * sentence as `MultiSelect` with a different affordance. Keeping it here means
 * one announcer, one notice treatment and one set of tokens instead of a fork.
 *
 * **It forwards its ref to the FIRST switch**, which is what makes react-hook-form's
 * `shouldFocusError` able to reach it at all: this kind was one of three that
 * took no ref, so an invalid toggle group was revealed on submit and never
 * focused. A wrapper `div` would have satisfied the call and left the operator
 * one Tab short of the control — the same reason `KeyValueEditor` focuses a row
 * rather than its own box.
 */
export const ToggleGroup = forwardRef<HTMLButtonElement, ToggleGroupProps>(function ToggleGroup(
  {
    id,
    className,
    label,
    items,
    value,
    onValueChange,
    isDisabled = false,
    isReadOnly = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const generatedId = useId();
  const groupId = id ?? generatedId;
  const { rows, isEmpty, toggle, announcement } = useToggleGroupLogic({
    items,
    value,
    onValueChange,
    isDisabled,
    isReadOnly,
  });

  return (
    <div
      id={groupId}
      role="group"
      aria-label={label}
      aria-describedby={describedBy}
      className={cn(TOGGLE_GROUP_CLASSES, className)}
    >
      {isEmpty ? <p className={SELECTION_NOTICE_CLASSES}>{t('toggleGroup.empty')}</p> : null}

      {rows.map((row, index) => (
        <ToggleGroupRow
          key={row.value}
          // Only the first row takes the ref: `shouldFocusError` calls `focus()`
          // once, and the top of the group is where reading it starts.
          switchRef={index === 0 ? ref : undefined}
          row={row}
          isReadOnly={isReadOnly}
          onToggle={toggle}
          onBlur={onBlur}
        />
      ))}

      {/* One live region for the whole control, always mounted — a region added
          with its first message is announced late or not at all. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
});

interface ToggleGroupRowProps {
  row: {
    value: string;
    label: string;
    description?: string;
    isChecked: boolean;
    isDisabled: boolean;
  };
  isReadOnly: boolean;
  switchRef: React.Ref<HTMLButtonElement> | undefined;
  onToggle: (value: string, isOn: boolean) => void;
  onBlur?: () => void;
}

/**
 * A module-scope component, not an inline closure: a component identity created
 * during render remounts its subtree on every pass, which React Compiler's
 * `static-components` rule forbids at error level — and here it would cost focus
 * on every toggle.
 */
function ToggleGroupRow({ row, isReadOnly, switchRef, onToggle, onBlur }: ToggleGroupRowProps) {
  const rowId = useId();
  const descriptionId = useId();

  return (
    <div className={TOGGLE_GROUP_ROW_CLASSES}>
      <div className={TOGGLE_GROUP_ROW_TEXT_CLASSES}>
        <Label htmlFor={rowId} className={TOGGLE_GROUP_LABEL_CLASSES}>
          {row.label}
        </Label>
        {row.description === undefined ? null : (
          <p id={descriptionId} className={TOGGLE_GROUP_DESCRIPTION_CLASSES}>
            {row.description}
          </p>
        )}
      </div>
      <Switch
        ref={switchRef}
        id={rowId}
        checked={row.isChecked}
        disabled={row.isDisabled}
        // NOT `disabled`: a read-only control stays focusable and answers the
        // click by saying it refuses (`useToggleGroupLogic`).
        aria-readonly={isReadOnly ? true : undefined}
        aria-describedby={row.description === undefined ? undefined : descriptionId}
        onBlur={onBlur}
        onCheckedChange={(isOn) => {
          onToggle(row.value, isOn);
        }}
      />
    </div>
  );
}
